import { Component, useState, type ErrorInfo, type ReactNode } from 'react'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import GcpCollapsible from '@/components/gcp/GcpCollapsible'
import GcpCopyButton from '@/components/gcp/GcpCopyButton'
import { buildReportMarkdown, formatUsd } from '@/components/gcp/gcpHelpers'
import {
  fetchGcpAnalysisXlsx,
  type GcpAnalysisResult,
  type GcpRecommendationsResult,
} from '@/services/gcpCosts'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

interface GcpAiRecommendationsProps {
  analysis: GcpAnalysisResult | null
  analysisJobId: number | null
  recommendations: GcpRecommendationsResult | null
  isRunning: boolean
  error: string | null
}

const RISK_STYLES: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
}

class RecommendationsErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Failed to render AI recommendations', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Recommendations finished but could not be displayed. Analysis results on this page are unchanged.
        </section>
      )
    }
    return this.props.children
  }
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function GcpAiRecommendationsView({
  analysis,
  analysisJobId,
  recommendations,
  isRunning,
  error,
}: GcpAiRecommendationsProps) {
  const [open, setOpen] = useState(true)

  if (!recommendations && !error && !isRunning) {
    return null
  }

  const handleDownload = async () => {
    const markdown = buildReportMarkdown(analysis, recommendations)
    const date = new Date().toISOString().slice(0, 10)
    const markdownBlob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
    downloadBlob(`gcp-cost-report-${date}.md`, markdownBlob)

    if (analysisJobId == null) {
      toast.error('Analysis export requires the original analysis run job.')
      return
    }

    try {
      const { blob, filename } = await fetchGcpAnalysisXlsx(analysisJobId)
      downloadBlob(filename, blob)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to download analysis spreadsheet'))
    }
  }

  const quickWins = recommendations?.quick_wins ?? []
  const extraDataNeeded = recommendations?.additional_data_needed ?? []
  const sortedRecs = [...(recommendations?.recommendations ?? [])].sort(
    (a, b) => a.priority - b.priority
  )

  const anthropicHint =
    error &&
    (error.toLowerCase().includes('anthropic') ||
      error.toLowerCase().includes('api_key') ||
      error.toLowerCase().includes('api key'))

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <h2 className="text-base font-semibold text-gray-900">AI recommendations</h2>
        {open ? (
          <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        )}
      </button>

      {open ? (
        <>
          {recommendations ? (
            <div className="mt-3 flex items-center justify-end">
              <Button variant="primary" gradient size="md" onClick={() => void handleDownload()}>
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                Download
              </Button>
            </div>
          ) : null}

          {isRunning && !recommendations ? (
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-gray-600">
              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
              Claude is analyzing 6 data sections…
            </p>
          ) : null}

          {error ? (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p>{error}</p>
              {anthropicHint ? (
                <p className="mt-1">
                  Ensure{' '}
                  <code className="text-xs">ANTHROPIC_API_KEY_GCP_COST_OPTIMIZER</code> is set in
                  the backend environment.
                </p>
              ) : null}
            </div>
          ) : null}

          {recommendations ? (
            <div className="mt-4 space-y-4">
              <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
                <p>{recommendations.summary}</p>
                <p className="mt-2 font-medium">
                  Latest monthly spend: {formatUsd(recommendations.latest_monthly_total_usd)} ·
                  Potential savings:{' '}
                  {formatUsd(recommendations.total_potential_savings_usd_monthly)}/mo
                </p>
              </div>

              {quickWins.length > 0 ? (
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-800">Quick wins</h3>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {quickWins.map((win, index) => (
                      <div
                        key={`${win.title}-${index}`}
                        className="min-w-[16rem] shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-3"
                      >
                        <p className="font-medium text-gray-900">{win.title}</p>
                        <p className="mt-1 text-sm text-gray-600">
                          {formatUsd(win.estimated_savings_usd_monthly)}/mo · {win.effort}
                        </p>
                        <div className="mt-2 flex items-start justify-between gap-2">
                          <pre className="max-w-full overflow-x-auto rounded bg-white px-2 py-1 text-xs text-gray-800">
                            {win.command}
                          </pre>
                          <GcpCopyButton text={win.command} label="Copy" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {sortedRecs.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-800">Ranked recommendations</h3>
                  {sortedRecs.map((rec) => (
                    <article
                      key={rec.priority}
                      className="rounded-lg border border-gray-200 p-4 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">#{rec.priority}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${RISK_STYLES[rec.risk] ?? RISK_STYLES.medium}`}
                        >
                          {rec.risk} risk
                        </span>
                        <span className="text-sm text-gray-700">
                          {rec.service}
                          {rec.resource ? ` · ${rec.resource}` : ''}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          ~{formatUsd(rec.estimated_savings_usd_monthly)}/mo
                        </span>
                        {rec.related_guardrail ? (
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                            {rec.related_guardrail}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3 space-y-2 text-sm text-gray-800">
                        <p>
                          <span className="font-medium">Issue:</span> {rec.issue}
                        </p>
                        <p>
                          <span className="font-medium">Recommendation:</span> {rec.recommendation}
                        </p>
                        <div>
                          <div className="mb-1 flex items-center justify-between">
                            <span className="font-medium">Implementation</span>
                            <GcpCopyButton text={rec.implementation} label="Copy" />
                          </div>
                          <pre className="overflow-x-auto rounded-md bg-gray-50 p-3 text-xs">
                            {rec.implementation}
                          </pre>
                        </div>
                        <p>
                          <span className="font-medium">Verification:</span> {rec.verification}
                        </p>
                        <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
                          <span className="font-medium">Data citation:</span> {rec.data_citation}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}

              {extraDataNeeded.length > 0 ? (
                <GcpCollapsible title="What would improve this analysis" defaultOpen={false}>
                  <ul className="list-disc space-y-2 pl-5 text-sm text-gray-800">
                    {extraDataNeeded.map((item, index) => (
                      <li key={`${item.data_type}-${index}`}>
                        <span className="font-medium">{item.data_type}:</span> {item.reason} (
                        {item.how_to_get})
                      </li>
                    ))}
                  </ul>
                </GcpCollapsible>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

export default function GcpAiRecommendations(props: GcpAiRecommendationsProps) {
  return (
    <RecommendationsErrorBoundary>
      <GcpAiRecommendationsView {...props} />
    </RecommendationsErrorBoundary>
  )
}
